import type { Block, Blocks } from '@w/content-blocks/src/block'

/**
 * The Blocks of the Lexical view's clipboard JSON (`application/x-lexical-editor`, `$generateJSONFromSelectedNodes`:
 * `{ namespace, nodes }` of `exportJSON` records), decoded as the Lexical view's paste does ($parseSerializedNode, then
 * `lexicalNodesToBlocks`, top-level inline nodes gathered into paragraphs) without Lexical. Covers rich text and registered document payload nodes;
 * Unknown foreign extensions fall back to other flavours. Question/alert controls are editor chrome, not document Blocks.
 */
export function lexicalClipboardBlocks(payload: unknown): Blocks | undefined {
  if (!_isRecord(payload) || !Array.isArray(payload.nodes)) return undefined
  const blocks = _decodeAll(payload.nodes)
  return blocks && _gatherInline(blocks)
}

type Serialized = Record<string, unknown>

function _decodeAll(nodes: readonly unknown[], parent?: Serialized): Block[] | undefined {
  const blocks: Block[] = []
  for (const node of nodes) {
    if (_isRecord(node) && ['question-control', 'alert-control'].includes(String(node.type))) continue
    const block = _isRecord(node) ? _decode(node, parent) : undefined
    if (!block) return undefined
    blocks.push(block)
  }
  return blocks
}

/** lexical-adapter.ts `convertNodeToBlock`, per serialized node type. */
function _decode(node: Serialized, parent: Serialized | undefined): Block | undefined {
  const children = () => _decodeAll(Array.isArray(node.children) ? node.children : [], node)
  const state = _isRecord(node.$) ? node.$ : {}
  const payload = _isRecord(node.payload) ? node.payload : node
  switch (node.type) {
    case 'markdown-block':
    case 'presentation':
    case 'inline-presentation':
    case 'content-block-comments':
    case 'page-meta-media':
      return _isRecord(payload.block) ? structuredClone(payload.block) as Block : undefined
    case 'media': {
      if (!['img', 'video', 'audio', 'file'].includes(String(node.mediaType)) || typeof node.src !== 'string') return undefined
      const block: Record<string, unknown> = { id: '', type: node.mediaType, src: node.patchSrc ?? node.src }
      for (const key of ['alt', 'width', 'caption', 'children', 'autoplay', 'name', 'size']) {
        if (node[key] !== undefined) block[key] = structuredClone(node[key])
      }
      return block as Block
    }
    case 'table':
    case 'tablerow':
    case 'tablecell':
    case 'columns':
    case 'column': {
      const content = children()
      if (!content) return undefined
      const type = ({ table: 'table', tablerow: 'tr', tablecell: 'td', columns: 'cols', column: 'col' } as const)[node.type]
      // Blocks cells own inline children; Lexical cells wrap each line in a paragraph
      // (lexical-adapter.ts tableCellChildrenToBlocks).
      const cellContent = node.type === 'tablecell' ? content.flatMap((block, index): Blocks => [
        ...(index > 0 ? [{ id: '', type: 'br' as const }] : []),
        ...(block.type === 'p' ? block.children : [block]),
      ]) : content
      return { id: '', type, children: cellContent,
        ...(node.type === 'column' ? { width: payload.width } : {}),
        ...(typeof node.colSpan === 'number' && node.colSpan > 1 ? { colSpan: node.colSpan } : {}),
        ...(typeof node.rowSpan === 'number' && node.rowSpan > 1 ? { rowSpan: node.rowSpan } : {}),
        ...(typeof node.headerState === 'number' && node.headerState > 0 ? { _isHeader: true } : {}),
      } as Block
    }
    case 'question': {
      const fields = (Array.isArray(node.children) ? node.children : []).filter(value => _isRecord(value))
        .filter(field => field.type === 'question-field')
      const role = (field: Serialized) => _isRecord(field.payload) ? field.payload.role : undefined
      const fieldBlocks = (field: Serialized) => _decodeAll(Array.isArray(field.children) ? field.children : [], field)
      const result = { ...structuredClone(payload), id: '', type: 'question' } as Record<string, unknown>
      for (const field of fields) {
        const content = fieldBlocks(field)
        if (!content) return undefined
        if (role(field) === 'prompt') { result.question = content; delete result.children }
        if (role(field) === 'explanation') {
          if (content.length > 0) result.explanation = content
          else delete result.explanation
        }
      }
      const options = fields.filter(field => role(field) === 'option')
      if (options.length > 0) result.opts = options.map(field => fieldBlocks(field))
      const answers = fields.filter(field => role(field) === 'accepted-answer')
      if (answers.length > 0) result.answer = answers.map(value => _nestedText(value))
      const criteria = fields.filter(field => role(field) === 'criterion')
      if (criteria.length > 0) result.criteria = criteria.map(value => _nestedText(value))
      const regexp = fields.find(field => role(field) === 'answer-regexp')
      if (regexp) {
        const text = _nestedText(regexp)
        if (text) result.answerRegexp = text
        else delete result.answerRegexp
      }
      const transcript = fields.find(field => role(field) === 'sr-transcript')
      if (transcript) result.answer = { ...(_isRecord(payload.answer) ? payload.answer : {}), transcript: _nestedText(transcript) }
      return result as Block
    }
    case 'question-field':
    case 'cloze-field':
    case 'alert-field': {
      const content = children()
      return content && { id: '', type: 'p', children: content }
    }
    case 'inline-text': {
      if (!_isRecord(payload.block)) return undefined
      const block = structuredClone(payload.block)
      if (block.type === 'em' || block.type === 'l') {
        const content = children()
        return content && { ...block, children: content } as Block
      }
      const text = _nestedText(node).trim()
      if (block.type === 'pages') return { ...block, ids: text.split(',').map(id => id.trim()).filter(Boolean) } as Block
      return { ...block, ...(block.subtype === 'date' ? {} : { uid: text.replace(/^@/, '').trim() }) } as Block
    }
    case 'rule':
    case 'questionGroup':
    case 'content-block-comment-ref': {
      if (!_isRecord(payload.block)) return undefined
      const content = children()
      return content && { ...structuredClone(payload.block), children: content } as Block
    }
    case 'review':
    case 'diff': {
      const content = children()
      const { $present: _present, inline: _inline, ...block } = payload
      return content && { ...structuredClone(block), children: content } as Block
    }
    case 'cloze': {
      const nodes = (Array.isArray(node.children) ? node.children : []).filter(value => _isRecord(value))
      const content = _decodeAll(nodes.filter(child => child.type !== 'cloze-field'), node)
      const result = { ...structuredClone(payload), type: 'cloze', id: '', children: content } as Record<string, unknown>
      for (const [role, key] of [['tip', 'tip'], ['answer-regexp', 'answerRegexp']]) {
        const field = nodes.find(child => child.type === 'cloze-field' && _isRecord(child.payload) && child.payload.role === role)
        if (field) {
          const text = _nestedText(field)
          if (text) result[key] = text
          else delete result[key]
        }
      }
      return content ? result as Block : undefined
    }
    case 'alert': {
      if (!_isRecord(payload.block)) return undefined
      const fields = (Array.isArray(node.children) ? node.children : []).filter(value => _isRecord(value))
      const title = fields.find(field => field.type === 'alert-field' && field.role === 'title')
      const body = fields.find(field => field.type === 'alert-field' && field.role === 'body')
      const content = body ? _decodeAll(Array.isArray(body.children) ? body.children : [], body) : []
      return content && { ...structuredClone(payload.block), ...(title ? { title: _nestedText(title) } : {}), children: content } as Block
    }
    case 'page-title':
      return typeof payload.title === 'string' ? { id: '', type: 'pageTitle', title: payload.title.trim() || 'Untitled page' } : undefined
    case 'page-meta': {
      if (!_isRecord(payload.block)) return undefined
      const fields = (Array.isArray(node.children) ? node.children : []).filter(value => _isRecord(value))
      const field = (role: string) => fields.find(item => _isRecord(item.payload) && item.payload.role === role)
      const title = field('title')
      const emoji = field('emoji')
      return { ...structuredClone(payload.block), title: title ? _nestedText(title).trim() : 'Untitled page',
        ...(emoji && _nestedText(emoji).trim() ? { emoji: _nestedText(emoji).trim() } : {}) } as Block
    }
    case 'page-meta-field':
    case 'quote-author': {
      const content = children()
      return content && { id: '', type: 'p', children: content }
    }
    case 'page':
      return typeof node.pageId === 'string' ? { id: '', type: node.pageLink ? 'pageLink' : 'page', pageId: node.pageId,
        ...(typeof node.spaceId === 'string' ? { spaceId: node.spaceId } : {}) } : undefined
    case 'text':
    case 'tab':
    case 'code-highlight': {
      if (typeof node.text !== 'string') return undefined
      const format = typeof node.format === 'number' ? node.format : 0
      return { id: '', type: 'chars', text: node.text ? [{ id: '', text: node.text }] : [], ...(format ? { format } : {}) }
    }
    case 'linebreak':
      return { id: '', type: 'br' }
    case 'horizontalrule':
      return { id: '', type: 'hr' }
    case 'paragraph': {
      const inline = children()
      const align = node.format
      return inline && { id: '', type: 'p', ...(align === 'center' || align === 'right' || align === 'left' ? { align } : {}), children: inline }
    }
    case 'heading': {
      const inline = children()
      const level = typeof node.tag === 'string' ? Math.trunc(Number(node.tag.replace('h', ''))) : Number.NaN
      return inline && Number.isInteger(level) ? ({ id: '', type: 'h', level, children: inline } as Block) : undefined
    }
    case 'quote': {
      const nodes = (Array.isArray(node.children) ? node.children : []).filter(value => _isRecord(value))
      const author = nodes.find(child => child.type === 'quote-author')
      const content = _decodeAll(nodes.filter(child => child.type !== 'quote-author'), node)
      const authorBlocks = author && _decodeAll(Array.isArray(author.children) ? author.children : [], author)
      return content && { id: '', type: 'quote', children: _gatherInline(content), ...(authorBlocks ? { author: authorBlocks } : {}) }
    }
    case 'link':
    case 'autolink':
    case 'content-blocks-link': {
      const inline = children()
      return inline && typeof node.url === 'string' ? { id: '', type: 'a', href: node.url, children: inline } : undefined
    }
    case 'list':
    case 'content-blocks-list': {
      const items = children()
      const listType = node.listType
      const subtype = state.contentBlockListSubtype === 'toggle' ? 'toggle' : listType === 'number' ? 'ordered' : listType === 'check' ? 'todo' : undefined
      const start = typeof node.start === 'number' ? node.start : 1
      return items && {
        id: '',
        type: 'list',
        ...(subtype ? { subtype } : {}),
        ...(listType === 'number' && start !== 1 ? { start } : {}),
        ...(state.contentBlockListSpread === true ? { spread: true } : {}),
        children: items,
      }
    }
    case 'listitem':
    case 'content-blocks-listitem': {
      const content = children()
      const done = parent?.listType === 'check' && typeof node.checked === 'boolean' ? node.checked : undefined
      return content && { id: '', type: 'li', ...(done === undefined ? {} : { done }), children: _gatherInline(content) }
    }
    case 'code': {
      const lang = typeof node.language === 'string' && node.language ? { lang: node.language } : {}
      if (Array.isArray(node.sourceBlocks)) return { id: '', type: 'pre', children: structuredClone(node.sourceBlocks as Blocks), ...lang }
      const text = _textContent(node)
      return text === undefined ? undefined : { id: '', type: 'pre', children: [{ id: '', type: 'chars', text: [{ id: '', text }] }], ...lang }
    }
    default:
      return undefined
  }
}

/** Runs of inline blocks gathered into paragraphs; block-level blocks kept in place. */
function _gatherInline(blocks: readonly Block[]): Block[] {
  const out: Block[] = []
  let inline: Block[] = []
  const flush = () => {
    if (inline.length > 0) out.push({ id: '', type: 'p', children: inline })
    inline = []
  }
  for (const block of blocks) {
    if (['chars', 'br', 'a', 'cloze', 'code', 'em', 'l', 'mention', 'pages', 'pageLink', 'commentRef'].includes(block.type)) inline.push(block)
    else {
      flush()
      out.push(block)
    }
  }
  flush()
  return out
}

/** A code node's `getTextContent()`: its text, tab and highlight leaves, a line break as `\n`. */
function _textContent(node: Serialized): string | undefined {
  let text = ''
  for (const child of Array.isArray(node.children) ? node.children : []) {
    if (!_isRecord(child)) return undefined
    if (child.type === 'linebreak') text += '\n'
    else if (typeof child.text === 'string' && (child.type === 'text' || child.type === 'tab' || child.type === 'code-highlight')) text += child.text
    else return undefined
  }
  return text
}

function _isRecord(value: unknown): value is Serialized {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function _nestedText(node: Serialized): string {
  if (typeof node.text === 'string') return node.text
  if (node.type === 'linebreak') return '\n'
  return (Array.isArray(node.children) ? node.children : []).filter(value => _isRecord(value)).map(value => _nestedText(value)).join('')
}
